import { LocalStorageKey, SessionStorageKey } from "@/common/AppConst";
import { AppConfigs } from "@/common/entity/AppConfigs";
import { IUser, User } from "@/common/entity/User";
import Vuex from "vuex";
import { logToConsole } from "@/common/LogHelper";
import Vue from "vue";
import { DEFAULT_COPYWRITING_LENGTH } from "@/pages/ai-copywriting/AiCopywritingPage";

Vue.use(Vuex);

/** 智能创作界面内容状态值 */
export type AiCopywritingInfos = {
  /** 描述输入框的内容 */
  desc: string;
  /** 文案字数输入框的值 */
  length: number;
  /** 文案风格选择器的选中值 */
  style: string;
  /** 文案生成结果的值 */
  result: string;
};

/** 智能创作界面内容状态值 */
export type AiCopywritingInfoOptions = {
  [key in keyof AiCopywritingInfos]?: AiCopywritingInfos[key];
};

/** 纠错界面内容状态值(只用于创建模式) */
export type CorrectInfos = {
  /** 标题输入框的值 */
  title: string;
  /** 内容输入框html */
  contentHtml: string;
  /** 是否是长文本模式 */
  isLongTextModel: boolean;
};
/** 纠错界面内容状态值(只用于创建模式) */
export type CorrectInfosOptions = {
  [key in keyof CorrectInfos]?: CorrectInfos[key];
};

/** vuex根state */
export type RootState = {
  /** 当前登录用户信息 */
  currUser: User | null;
  /** app配置信息 */
  appConfigs: AppConfigs | null;
  /** 是否显示登录输入框 */
  showLoginPanel: boolean;
  /** 智能创作界面内容状态值 */
  aiCopywritingInfos: AiCopywritingInfos;
  /** 纠错界面内容状态值(只用于创建模式) */
  correctInfos: CorrectInfos | null;
};

/** vuex根state名 */
export const RootStateNames: { [key in keyof RootState]: key } = {
  /** 当前登录用户信息 */
  currUser: "currUser",
  /** app配置信息 */
  appConfigs: "appConfigs",
  /** 是否显示登录输入框 */
  showLoginPanel: "showLoginPanel",
  /** 智能创作界面内容状态值 */
  aiCopywritingInfos: "aiCopywritingInfos",
  /** 纠错界面内容状态值(只用于创建模式) */
  correctInfos: "correctInfos",
};

/** vuex根getter名 */
export const RootGetterNames = {
  /**
   * 显示在用户信息那里的用户名，默认显示用户昵称，如果昵称为空显示手机号码，未登录或没有昵称和手机号则返回空字符串
   * 返回类型为`string`
   */
  userName: "userName",
  /**
   * 当前是否处于已登录状态，返回类型为`boolean`
   */
  isLogined: "isLogined",
  /**
   * 当前剩余的AI功能使用次数，返回类型为`number`，未登录时返回null
   */
  aiFuncCount: "aiFuncCount",
};

/** vuex根mutation名 */
export const RootMutationNames = {
  /**
   * 设置当前登录用户信息，参数为{@link IUser}或`null`类型
   * @param IUser currUser 当前用户信息
   */
  setCurrUser: "setCurrUser",
  /**
   * 设置程序配置信息
   * @param AppConfigs appConfigs 程序配置信息
   */
  setAppConfigs: "setAppConfigs",
  /**
   * 是否显示登录dialog
   * @param boolean isShow 是否显示
   */
  setShowLoginPanel: "setShowLoginPanel",
  /**
   * 更新智能创作界面内容状态值
   * @param AiCopywritingInfoOptions infos 只更新此参数中存在的字段，其他字段不变
   */
  updateAiCopywritingInfos: "updateAiCopywritingInfos",
  /**
   * 更新纠错界面内容状态值（只用于创建模式）
   * @param CorrectInfosOptions|null infos 只更新此参数中存在的字段，其他字段不变，
   * 为null时把状态值设为null
   */
  updateCorrectInfos: "updateCorrectInfos",
};

/** vuex根action名 */
export const RootActionNames = {
  /**
   * 退出登录
   */
  logout: "logout",
};

//获取本地当前用户信息
function getLocalUser(): User | null {
  try {
    const currUserJson: string | null = localStorage.getItem(
      LocalStorageKey.currUser
    );
    logToConsole("store localStorage appConfigs=", currUserJson);
    const iCurrUser: IUser | null = currUserJson
      ? (JSON.parse(currUserJson) as IUser)
      : null;
    return iCurrUser?.id ? new User(iCurrUser) : null;
  } catch (error) {
    return null;
  }
}

//获取本地程序配置信息
function getLocalAppConfigs(): AppConfigs | null {
  const appConfigsJson: string | null = localStorage.getItem(
    LocalStorageKey.appConfigs
  );
  logToConsole("store localStorage appConfigs=", appConfigsJson);
  return appConfigsJson ? (JSON.parse(appConfigsJson) as AppConfigs) : null;
}

//根store实例
export const RootStore = new Vuex.Store<RootState>({
  strict: true,
  state: {
    currUser: getLocalUser(),
    appConfigs: getLocalAppConfigs(),
    showLoginPanel: false,
    aiCopywritingInfos: {
      desc: "",
      length: DEFAULT_COPYWRITING_LENGTH,
      style: "",
      result: "",
    },
    correctInfos: null,
  },
  getters: {
    /**
     * @see RootGetterNames.userName
     */
    [RootGetterNames.userName](state: RootState): string {
      return state.currUser
        ? state.currUser.nickname || state.currUser.phone || `已登录(uid：${state.currUser.inviteCode || state.currUser.id.toString(16)})`
        : "";
    },
    /**
     * @see RootGetterNames.isLogined
     */
    [RootGetterNames.isLogined](state: RootState): boolean {
      return !!state.currUser?.accessToken;
    },
    /**
     * @see RootGetterNames.aiFuncCount
     */
    [RootGetterNames.aiFuncCount](state: RootState): number | null {
      return state.currUser?.correctCount || null;
    },
  },
  mutations: {
    /**
     * @see RootMutationNames.setAppConfig
     */
    [RootMutationNames.setAppConfigs](state, appConfigs: AppConfigs | null) {
      logToConsole(
        `rootStore mutations ${RootMutationNames.setAppConfigs} appConfigs=`,
        appConfigs
      );
      localStorage.setItem(
        LocalStorageKey.appConfigs,
        JSON.stringify(appConfigs)
      );
      state.appConfigs = appConfigs;
    },
    /**
     * @see RootMutationNames.setCurrUser
     */
    [RootMutationNames.setCurrUser](state, currUser: IUser | null) {
      logToConsole(
        `rootStore mutations ${RootMutationNames.setCurrUser} currUser=`,
        currUser
      );
      localStorage.setItem(LocalStorageKey.currUser, JSON.stringify(currUser));
      state.currUser = currUser ? new User(currUser) : null;
    },
    /**
     * @see RootMutationNames.setShowLoginPanel
     */
    [RootMutationNames.setShowLoginPanel](state, isShow?: boolean) {
      state.showLoginPanel = !!isShow;
    },
    /**
     * @see RootMutationNames.updateAiCopywritingInfos
     */
    [RootMutationNames.updateAiCopywritingInfos](
      state: RootState,
      infos: AiCopywritingInfoOptions
    ) {
      if (infos.desc !== undefined) state.aiCopywritingInfos.desc = infos.desc;
      if (infos.length !== undefined)
        state.aiCopywritingInfos.length = infos.length;
      if (infos.result !== undefined)
        state.aiCopywritingInfos.result = infos.result;
      if (infos.style !== undefined)
        state.aiCopywritingInfos.style = infos.style;
    },
    [RootMutationNames.updateCorrectInfos](
      state: RootState,
      infos: CorrectInfosOptions | null
    ) {
      if (infos === null) {
        state.correctInfos = null;
        return;
      }
      if (state.correctInfos === null)
        state.correctInfos = {
          title: "",
          contentHtml: "",
          isLongTextModel: false,
        };
      if (infos.contentHtml !== undefined)
        state.correctInfos.contentHtml = infos.contentHtml;
      if (infos.isLongTextModel !== undefined)
        state.correctInfos.isLongTextModel = infos.isLongTextModel;
      if (infos.title !== undefined) state.correctInfos.title = infos.title;
      if (!state.correctInfos.title && !state.correctInfos.contentHtml)
        state.correctInfos = null;
    },
  },
  actions: {
    /**
     * @see RootActionNames.logout
     */
    [RootActionNames.logout]({ commit }) {
      logToConsole(`rootStore actions ${RootActionNames.logout}`);
      sessionStorage.removeItem(SessionStorageKey.checkArticleId);
      commit(RootMutationNames.setCurrUser, null);
    },
  },
});
